import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, RotateCcw } from 'lucide-react';
import {
  AI_LENGTH_MAX_CHARS,
  AI_LENGTH_MAX_WORDS,
  AI_LENGTH_TARGETS,
  DEFAULT_AI_LENGTH_GUIDELINES,
} from '@mg/shared';
import type {
  AiLengthGuideline,
  AiLengthGuidelines,
  AiLengthTargetId,
  PlatformSettings,
} from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useIsTenantAdmin } from '../../auth/useWorkspaceAdmin';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Field';
import { Button } from '../../components/ui/Button';
import { CHANGES_SAVED, useToast } from '../../components/ui/toast';
import { useFooterSave } from '../../components/settings/footerSave';

/** The three tunable fields per destination, with their column label, hint and accepted bound. */
const FIELDS: {
  key: keyof AiLengthGuideline;
  label: string;
  max: number;
  hard?: boolean;
}[] = [
  { key: 'targetWords', label: 'Target words', max: AI_LENGTH_MAX_WORDS },
  { key: 'targetChars', label: 'Target characters', max: AI_LENGTH_MAX_CHARS },
  { key: 'maxChars', label: 'Hard max characters', max: AI_LENGTH_MAX_CHARS, hard: true },
];

/** Draft values are strings so an empty box ("no limit") is representable while editing. */
type Draft = Record<string, string>;

const cellKey = (id: AiLengthTargetId, field: keyof AiLengthGuideline) => `${id}.${field}`;

const toInput = (n: number | null): string => (n === null ? '' : String(n));

function draftFrom(guidelines: AiLengthGuidelines): Draft {
  const draft: Draft = {};
  for (const { id } of AI_LENGTH_TARGETS) {
    for (const { key } of FIELDS) draft[cellKey(id, key)] = toInput(guidelines[id][key]);
  }
  return draft;
}

/**
 * AI Assist content-length guidance — install-wide, one set for every Brand Workspace. Visible to
 * all members so they know what the composer is targeting; editable only by a Tenant Admin (the
 * API enforces that too). Its own tab on App Settings, where the page's sticky Save footer saves it.
 */
export function AiLengthGuidelinesCard() {
  const query = useQuery({
    queryKey: ['platform-settings'],
    queryFn: ({ signal }) => api.getPlatformSettings(signal),
  });

  return (
    <Card>
      <CardHeader
        title="AI Assist content length"
        description="When AI Assist generates copy in the New Post and Edit Post composer, it targets these lengths for the post's publishing platform. Targets are guidelines, not strict requirements — the AI is told to prioritize natural, high-quality writing while staying close to them. A hard maximum is different: it is the platform's absolute limit and is never exceeded."
      />
      <CardBody>
        {query.isLoading ? (
          <p className="text-sm text-ink-faint">Loading length guidance…</p>
        ) : query.isError ? (
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load length guidance.</p>
        ) : query.data ? (
          <AiLengthGuidelinesForm settings={query.data} />
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * Seeded from the loaded `settings` prop and diffed against it each render, so after a save (which
 * refetches and flows the new values back in) the form is no longer dirty and "Saved" sticks —
 * same pattern as PlatformSettingsCard, no remount key needed.
 */
function AiLengthGuidelinesForm({ settings }: { settings: PlatformSettings }) {
  const canEdit = useIsTenantAdmin();
  const queryClient = useQueryClient();
  const baseId = useId();

  const saved = settings.aiLengthGuidelines;
  const [draft, setDraft] = useState<Draft>(() => draftFrom(saved));
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const savedDraft = draftFrom(saved);
  const dirty = Object.keys(savedDraft).some((k) => (draft[k] ?? '') !== savedDraft[k]);

  /** Non-empty values must be whole numbers in range; empty means "no limit". */
  const invalid = AI_LENGTH_TARGETS.flatMap(({ id, label }) =>
    FIELDS.flatMap(({ key, label: fieldLabel, max }) => {
      const raw = (draft[cellKey(id, key)] ?? '').trim();
      if (raw === '') return [];
      const n = Number(raw);
      if (!Number.isInteger(n) || n <= 0 || n > max) {
        return [`${label} · ${fieldLabel} must be a whole number between 1 and ${max}.`];
      }
      return [];
    }),
  );

  const save = useMutation({
    mutationFn: () => {
      // Send only the destinations that actually changed; '' clears a field (null).
      const patch: Partial<Record<AiLengthTargetId, Partial<AiLengthGuideline>>> = {};
      for (const { id } of AI_LENGTH_TARGETS) {
        const entry: Partial<AiLengthGuideline> = {};
        let touched = false;
        for (const { key } of FIELDS) {
          const k = cellKey(id, key);
          const next = (draft[k] ?? '').trim();
          if (next === savedDraft[k]) continue;
          entry[key] = next === '' ? null : Number(next);
          touched = true;
        }
        if (touched) patch[id] = entry;
      }
      return api.updatePlatformSettings({ aiLengthGuidelines: patch });
    },
    onSuccess: async (updated) => {
      setError(null);
      setJustSaved(true);
      // Re-seed from the server's normalized values, not just the refetch: a typed "0075"
      // persists as 75, and leaving the raw string in the draft would keep the form looking
      // dirty forever (so "Saved" would never stick and Save would stay enabled).
      setDraft(draftFrom(updated.aiLengthGuidelines));
      await queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not save length guidance.'),
  });

  const toast = useToast();
  /** Saves the change; resolves to whether it worked (a failure shows the server's sentence here). */
  const saveChanges = async (): Promise<boolean> => {
    setError(null);
    try {
      await save.mutateAsync();
      return true;
    } catch {
      return false;
    }
  };
  // On App Settings the page's sticky Save footer saves this form, and its own Save stands down. A
  // box out of range is a change that can't be saved as it stands. Discarding puts the saved values
  // back, which also undoes a Restore recommended that was not saved.
  const { inFooter } = useFooterSave({
    id: 'ai-length-guidelines',
    dirty,
    canSave: invalid.length === 0,
    save: saveChanges,
    discard: () => {
      setError(null);
      setJustSaved(false);
      setDraft(draftFrom(saved));
    },
  });

  const setCell = (id: AiLengthTargetId, key: keyof AiLengthGuideline, value: string) => {
    setJustSaved(false);
    setDraft((prev) => ({ ...prev, [cellKey(id, key)]: value }));
  };

  return (
    <div className="space-y-5">
      <div className="space-y-4">
        {AI_LENGTH_TARGETS.map(({ id, label }) => (
          <div key={id} className="rounded-xl border border-line-soft p-4">
            <p className="mb-3 text-sm font-semibold text-ink">{label}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {FIELDS.map(({ key, label: fieldLabel, hard }) => {
                const inputId = `${baseId}-${id}-${key}`;
                return (
                  <div key={key}>
                    <label
                      htmlFor={inputId}
                      className={`mb-1 block text-xs font-medium ${
                        hard ? 'text-amber-700 dark:text-warning-dark' : 'text-ink-muted'
                      }`}
                    >
                      {fieldLabel}
                    </label>
                    <Input
                      id={inputId}
                      type="number"
                      inputMode="numeric"
                      min={1}
                      placeholder="none"
                      value={draft[cellKey(id, key)] ?? ''}
                      disabled={!canEdit || save.isPending}
                      onChange={(e) => setCell(id, key, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-ink-faint">
        Leave a box empty for no limit. Video and Podcast posts have no length guidance — they
        generate unconstrained. Guidance is applied when AI Assist writes or expands a full post,
        not when it drafts an outline or rewrites a single section.
      </p>

      {invalid.length > 0 && (
        <ul className="space-y-1 text-sm text-red-600 dark:text-error-dark">
          {invalid.map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-3">
          {!inFooter && (
            <Button
              onClick={() =>
                void saveChanges().then((ok) => {
                  if (ok) toast.show(CHANGES_SAVED);
                })
              }
              loading={save.isPending}
              disabled={save.isPending || !dirty || invalid.length > 0}
            >
              Save
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setJustSaved(false);
              setDraft(draftFrom(DEFAULT_AI_LENGTH_GUIDELINES));
            }}
            disabled={save.isPending}
          >
            <RotateCcw className="mr-1.5 h-4 w-4" />
            Restore recommended
          </Button>
          {justSaved && !dirty && (
            <span className="flex items-center gap-1 text-sm text-success dark:text-success-dark">
              <Check className="h-4 w-4" />
              Saved
            </span>
          )}
          {error && <span className="text-sm text-red-600 dark:text-error-dark">{error}</span>}
        </div>
      ) : (
        <p className="text-xs text-ink-faint">Only a Tenant Admin can change these.</p>
      )}
    </div>
  );
}
